import React, { useEffect, useState, useMemo } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Bell, CheckCircle, Warning, Clock, Path, UserCircle,
  ArrowsClockwise, Timer,
} from "@phosphor-icons/react";

const fmtElapsed = (secs) => {
  if (secs == null) return "—";
  if (secs < 60) return `${Math.round(secs)}s`;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs - m * 60);
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// ============================================================
// Iter 164 · Push Alert History
// Shows every URGENT / EMERGENCY SR alert with its full cascade
// timeline (L1 → L2 → L3) and per-agent response-time stats so
// supervisors can spot slow responders.
// ============================================================
export default function SrAlertHistory() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/operations/sr-alerts/history?days=${days}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const stats = data?.agent_stats || [];
  const alerts = data?.alerts || [];

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">
              Operations · Push Alerts
            </div>
            <h1 className="font-heading text-3xl mt-1 flex items-center gap-2">
              <Bell size={22} weight="duotone" color="#7A2E18"/>
              Urgent SR Push History
            </h1>
            <div className="text-[11px] text-[#5C5A55] mt-1">
              Every URGENT / EMERGENCY customer SR that was pushed to a
              field agent. Track cascade timing and identify slow responders.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select value={days} onChange={e => setDays(Number(e.target.value))}
                    data-testid="alert-history-range"
                    className="h-9 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
              <option value={1}>Last 24 h</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button onClick={load} disabled={loading}
                    data-testid="alert-history-refresh"
                    className="h-9 px-3 rounded-md border border-[#E5E2D9] text-sm inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
              <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""}/>
              Refresh
            </button>
          </div>
        </div>

        {/* Summary tiles */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <Tile label="Alerts sent" value={data.total_alerts} tone="default"/>
            <Tile label="Accepted"   value={data.accepted}      tone="success"/>
            <Tile label="Exhausted"  value={data.exhausted}     tone="danger"/>
            <Tile label="In progress" value={data.pending}       tone="warn"/>
          </div>
        )}

        {/* Per-agent response stats */}
        {stats.length > 0 && (
          <section className="bg-white border border-[#E5E2D9] rounded-md mb-5"
                   data-testid="agent-stats">
            <header className="px-4 py-3 border-b border-[#F0ECE3]">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#8A8780] font-semibold">
                Responder scoreboard
              </div>
              <div className="font-heading text-base">
                Fastest agents — {stats.length} responder{stats.length === 1 ? "" : "s"}
              </div>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">
                  <tr>
                    <th className="text-left px-4 py-2">Agent</th>
                    <th className="text-right px-4 py-2">Accepted</th>
                    <th className="text-right px-4 py-2">Avg response</th>
                    <th className="text-right px-4 py-2">Fastest</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0ECE3]">
                  {stats.map(s => (
                    <tr key={s.id} data-testid={`agent-stat-${s.id}`}>
                      <td className="px-4 py-2 font-medium inline-flex items-center gap-2">
                        <UserCircle size={16} weight="duotone" color="#2D5A27"/>
                        {s.name}
                      </td>
                      <td className="px-4 py-2 text-right">{s.accepted}</td>
                      <td className="px-4 py-2 text-right">
                        <ResponseTag secs={s.avg_response_s}/>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <ResponseTag secs={s.fastest_s}/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Alert list */}
        <section className="bg-white border border-[#E5E2D9] rounded-md">
          <header className="px-4 py-3 border-b border-[#F0ECE3]">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#8A8780] font-semibold">
              Timeline
            </div>
            <div className="font-heading text-base">
              {alerts.length} alert{alerts.length === 1 ? "" : "s"}
            </div>
          </header>
          {alerts.length === 0 && !loading && (
            <div className="p-8 text-center text-sm text-[#8A8780] italic">
              No urgent-SR alerts fired in this window.
            </div>
          )}
          <div className="divide-y divide-[#F0ECE3]">
            {alerts.map(a => (
              <AlertRow key={a.id} a={a}
                        expanded={expanded === a.id}
                        onToggle={() => setExpanded(expanded === a.id ? null : a.id)}/>
            ))}
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function Tile({ label, value, tone }) {
  const cls = {
    default: "bg-white border-[#E5E2D9]",
    success: "bg-[#EFF5EC] border-[#B5D5AC]",
    warn:    "bg-[#FDF6E4] border-[#F0DDB2]",
    danger:  "bg-[#FBEBE5] border-[#EFC7B6]",
  }[tone] || "bg-white border-[#E5E2D9]";
  return (
    <div className={`border rounded-md p-4 ${cls}`}
         data-testid={`alert-tile-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">
        {label}
      </div>
      <div className="font-heading text-3xl mt-1">{value}</div>
    </div>
  );
}

function ResponseTag({ secs }) {
  if (secs == null) return <span className="text-[#8A8780]">—</span>;
  const tone = secs < 30 ? "success" : secs < 90 ? "warn" : "danger";
  const cls = {
    success: "bg-[#EFF5EC] text-[#1A3317] border-[#B5D5AC]",
    warn:    "bg-[#FDF6E4] text-[#5A4600] border-[#F0DDB2]",
    danger:  "bg-[#FBEBE5] text-[#7A2E18] border-[#EFC7B6]",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[11px] font-semibold ${cls}`}>
      <Timer size={10}/>{fmtElapsed(secs)}
    </span>
  );
}

function AlertRow({ a, expanded, onToggle }) {
  const status = a.accepted_at ? "accepted"
                : a.cascade_ended_reason === "exhausted" ? "exhausted"
                : "pending";
  const statusCls = {
    accepted:  "bg-[#EFF5EC] text-[#1A3317] border-[#B5D5AC]",
    exhausted: "bg-[#FBEBE5] text-[#7A2E18] border-[#EFC7B6]",
    pending:   "bg-[#FDF6E4] text-[#5A4600] border-[#F0DDB2]",
  }[status];
  const statusIcon = { accepted: CheckCircle, exhausted: Warning, pending: Clock }[status];
  const StatusIcon = statusIcon;
  return (
    <div data-testid={`alert-row-${a.id}`}>
      <button onClick={onToggle}
              data-testid={`alert-toggle-${a.id}`}
              className="w-full text-left px-4 py-3 hover:bg-[#F7F6F2] grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
        <div className="md:col-span-3 min-w-0">
          <div className="text-[10px] font-mono text-[#8A8780]">{a.request_no}</div>
          <div className="font-medium text-sm truncate">{a.customer_name || "—"}</div>
          <div className="text-[10px] text-[#5C5A55]">{fmtDate(a.ts)}</div>
        </div>
        <div className="md:col-span-2">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-[0.14em] ${statusCls}`}>
            <StatusIcon size={11} weight="fill"/>{status}
          </span>
        </div>
        <div className="md:col-span-3 text-[11px] text-[#5C5A55]">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">Cascade</div>
          <div>
            L{a.final_level ?? a.current_level ?? 1} of {a.candidates?.length || 0}
            {a.escalations?.length > 1 && ` · ${a.escalations.length} pushes`}
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">Accepted by</div>
          <div className="text-[11px]">
            {a.accepted_by_name || <span className="text-[#8A8780] italic">nobody yet</span>}
          </div>
        </div>
        <div className="md:col-span-2 text-right">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780]">Response</div>
          <ResponseTag secs={a.response_seconds}/>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-2 bg-[#F7F6F2] border-t border-[#F0ECE3]"
             data-testid={`alert-detail-${a.id}`}>
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780] font-semibold mb-2">
            Cascade timeline
          </div>
          <div className="space-y-1.5">
            {(a.escalations || []).map(e => (
              <div key={e.level}
                   className="flex items-center gap-2 text-[12px] bg-white border border-[#E5E2D9] rounded px-3 py-2">
                <span className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${
                  e.level === 1 ? "bg-[#2D5A27] text-white"
                  : e.level === 2 ? "bg-[#D4A017] text-white"
                  : "bg-[#C25D38] text-white"
                }`}>
                  L{e.level}
                </span>
                <UserCircle size={14} color="#5C5A55"/>
                <span className="font-medium">{e.agent_name}</span>
                <span className="text-[#8A8780]">·</span>
                <span className="inline-flex items-center gap-0.5"><Path size={10}/>{e.km ?? "?"} km</span>
                {e.eta_min && (
                  <span className="text-[#8A8780]">· ~{Math.round(e.eta_min)} min ETA</span>
                )}
                <span className="text-[#8A8780]">·</span>
                <span className="text-[10px]">{fmtDate(e.ts)}</span>
                {e.push_count > 0
                  ? <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-[#EFF5EC] text-[#1A3317] border border-[#B5D5AC]">
                      {e.push_count} device{e.push_count === 1 ? "" : "s"} pushed
                    </span>
                  : <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-[#FBEBE5] text-[#7A2E18] border border-[#EFC7B6] italic">
                      No FCM token
                    </span>}
              </div>
            ))}
          </div>
          {a.candidates && a.candidates.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780] font-semibold mb-1">
                All candidates ranked
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {a.candidates.map((c, i) => (
                  <span key={c.id}
                        className="text-[10px] px-1.5 py-0.5 rounded-full border border-[#E5E2D9] bg-white">
                    #{i + 1} {c.name} · {c.km ?? "?"} km
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
